// Shows the result of a script's own fetch the same way a regular form submission's flash shows:
// as a toast in the layout's .toast-container (see application.html.erb), using the same markup,
// so toast_controller.js shows and hides it exactly like a flash rendered by the server.
//
// submitAndReport is the one path every fetch-based write in this app goes through (the issue
// list's row actions, a dashboard drag, an on-call calendar drag), so a success, a refusal (the
// read-only demo, a missing permission), and a plain failure each say so instead of failing
// silently. It reads whichever kind of response comes back:
//
// - JSON ({ ok, error }): an endpoint built for a script, and DemoMode's refusal of any JSON write.
// - HTML: a regular action's redirect, which fetch follows to a full page whose flash toasts
//   (already rendered there, and consumed from the session by that render) are copied over here.
const GENERIC_FAILURE = "That change couldn't be saved. Reload the page and try again."

export function showToast(message, type = "notice") {
  const container = toastContainer()
  const toast = document.createElement("div")
  toast.className = `toast align-items-center text-bg-${type === "alert" ? "danger" : "success"} border-0`
  toast.setAttribute("role", "alert")
  toast.setAttribute("aria-live", "assertive")
  toast.setAttribute("aria-atomic", "true")
  toast.dataset.controller = "toast"
  toast.dataset.bsAutohide = "true"
  toast.dataset.bsDelay = "5000"

  const row = document.createElement("div")
  row.className = "d-flex"
  const body = document.createElement("div")
  body.className = "toast-body"
  body.textContent = message
  const close = document.createElement("button")
  close.type = "button"
  close.className = "btn-close btn-close-white me-2 m-auto"
  close.dataset.bsDismiss = "toast"
  close.setAttribute("aria-label", "Close")
  row.append(body, close)
  toast.append(row)
  container.append(toast)
}

// Resolves to true only when the change was actually saved: a response that isn't ok, a JSON
// { ok: false }, or a followed redirect carrying an alert flash all count as refused, so a caller
// that already moved something on screen (a dragged widget or calendar block) knows to put it back.
export async function submitAndReport(url, options = {}) {
  let response
  try {
    response = await fetch(url, options)
  } catch {
    showToast(GENERIC_FAILURE, "alert")
    return false
  }

  const contentType = response.headers.get("Content-Type") || ""

  if (contentType.includes("application/json")) {
    const result = await response.json().catch(() => ({}))
    const saved = response.ok && result.ok !== false
    if (!saved) showToast(result.error || GENERIC_FAILURE, "alert")
    return saved
  }

  if (contentType.includes("text/html")) {
    const html = await response.text()
    const toasts = new DOMParser().parseFromString(html, "text/html").querySelectorAll(".toast-container .toast")
    toasts.forEach((toast) => toastContainer().append(document.importNode(toast, true)))
    const refused = [ ...toasts ].some((toast) => toast.classList.contains("text-bg-danger"))
    if (!response.ok && toasts.length === 0) showToast(GENERIC_FAILURE, "alert")
    return response.ok && !refused
  }

  if (!response.ok) showToast(GENERIC_FAILURE, "alert")
  return response.ok
}

function toastContainer() {
  let container = document.querySelector(".toast-container")
  if (!container) {
    container = document.createElement("div")
    container.className = "toast-container position-fixed top-0 end-0 p-3"
    document.body.append(container)
  }
  return container
}
